import { FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link, type Location } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/auth";
import { useAuth } from "../../../providers/AuthProvider";
import { storage } from "../../shared/api/storage";
import { AUTH_NOTICE_KEY, AUTH_REDIRECT_KEY } from "../../../providers/AuthProvider";
import { AuthFormBase } from "../components/AuthFormBase";
import { PasswordField } from "../../../components/PasswordField";
import { SignIn } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { PhoneField } from "../../../components/PhoneField";
import { buildE164, DEFAULT_PHONE_COUNTRY, resolvePhoneCountry } from "../../../phone/countryData";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { getNextPathFromSearch, sanitizeRedirectPath } from "../../../utils/navigation";

const primaryButtonClasses = "pm-btn pm-btn-primary w-full";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const fieldClasses = "flex flex-col gap-2";
const fieldHeaderClasses = "flex items-center justify-between";
const switchClasses = "text-xs font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300";
const linkClasses = "font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300";
const optionsContainerClasses = "flex items-center justify-end text-sm text-slate-600 dark:text-slate-400";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, login } = useAuth();

  const [mode, setMode] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { country: resolvedCountry, localDigits } = useMemo(
    () => resolvePhoneCountry(phoneInput, DEFAULT_PHONE_COUNTRY),
    [phoneInput]
  );

  const phoneIdentifier = useMemo(() => {
    if (mode !== "phone") {
      return "";
    }
    if (!localDigits) {
      return "";
    }
    return buildE164(resolvedCountry, localDigits);
  }, [localDigits, mode, resolvedCountry]);

  const canSubmit = useMemo(() => {
    if (isSubmitting || password.trim().length === 0) {
      return false;
    }
    if (mode === "email") {
      return email.trim().length > 0;
    }
    return phoneIdentifier.length > 0;
  }, [email, isSubmitting, mode, password, phoneIdentifier]);

  const nextFromQuery = useMemo(() => getNextPathFromSearch(location.search), [location.search]);

  const [redirectPath, setRedirectPath] = useState(() => {
    const fromLocation = (location.state as { from?: Location })?.from;
    const stateRedirect = sanitizeRedirectPath(
      fromLocation ? `${fromLocation.pathname}${fromLocation.search ?? ""}${fromLocation.hash ?? ""}` : null
    );
    const storedRedirect = sanitizeRedirectPath(storage.get<string>(AUTH_REDIRECT_KEY));
    return nextFromQuery ?? storedRedirect ?? stateRedirect ?? "/dashboard";
  });

  useEffect(() => {
    const notice = storage.get<string>(AUTH_NOTICE_KEY);
    if (notice) {
      setError(notice);
      storage.remove(AUTH_NOTICE_KEY);
    }

  }, []);

  useEffect(() => {
    if (nextFromQuery) {
      storage.set(AUTH_REDIRECT_KEY, nextFromQuery);
      setRedirectPath(nextFromQuery);
      return;
    }
    const storedRedirect = sanitizeRedirectPath(storage.get<string>(AUTH_REDIRECT_KEY));
    if (storedRedirect) {
      setRedirectPath(storedRedirect);
    }
  }, [nextFromQuery]);

  useEffect(() => {
    if (accessToken) {
      navigate(redirectPath, { replace: true });
      storage.remove(AUTH_REDIRECT_KEY);
    }
  }, [accessToken, navigate, redirectPath]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (password.trim().length === 0) {
        setError(t("auth.login.password_required"));
        return;
      }

      if (mode === "email") {
        if (!email.trim()) {
          setError(t("auth.login.email_required"));
          return;
        }
        await login({ email, identifier: email, password });
      } else {
        if (!phoneIdentifier) {
          setError(t("auth.login.phone_required"));
          return;
        }
        await login({
          email: phoneIdentifier,
          identifier: phoneIdentifier,
          password
        });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.body?.message ?? t("auth.login.invalid_credentials"));
      } else {
        setError(t("common.error.unexpected"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormBase
      onSubmit={handleSubmit}
      error={error}
      title={t("auth.login.welcome_back")}
      subtitle={t("auth.signin.to_continue")}
      auxiliaryContent={
        <div className={optionsContainerClasses}>
          <Link to="/forgot-password" className={linkClasses}>
            {t("auth.login.forgot_password_link")}
          </Link>
        </div>
      }
      submitButton={
        <button
          type="submit"
          className={primaryButtonClasses}
          disabled={!canSubmit}
        >
          {isSubmitting ? (
            t("auth.login.signing_in")
          ) : (
            <>
              <SignIn {...defaultIconProps} aria-hidden />
              {t("auth.signin.button")}
            </>
          )}
        </button>
      }
      socialDisabled={isSubmitting}
    >
      {mode === "email" ? (
        <div className={fieldClasses}>
          <div className={fieldHeaderClasses}>
            <FormFieldLabel label={t("forms.email.label")} />
            <button
              type="button"
              className={switchClasses}
              onClick={() => setMode("phone")}
              disabled={isSubmitting}
            >
              {t("auth.login.switch_to_phone")}
            </button>
          </div>
        <input
          id="login-email"
          type="email"
          inputMode="email"
          className={inputClasses}
          autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            disabled={isSubmitting}
            placeholder={t("forms.email.placeholder")}
          />
        </div>
      ) : (
        <div className={fieldClasses}>
          <div className={fieldHeaderClasses}>
            <FormFieldLabel label={t("forms.phone.label")} />
            <button
              type="button"
              className={switchClasses}
              onClick={() => setMode("email")}
              disabled={isSubmitting}
            >
              {t("auth.login.switch_to_email")}
            </button>
          </div>
          <PhoneField
            id="login-phone"
            value={phoneInput}
            onChange={setPhoneInput}
            disabled={isSubmitting}
            required
          />
        </div>
      )}

      <div className={fieldClasses}>
        <label htmlFor="login-password">
          <FormFieldLabel label={t("forms.password.label")} />
        </label>
        <PasswordField
          id="login-password"
          className={inputClasses}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
          disabled={isSubmitting}
        />
      </div>
    </AuthFormBase>
  );
}
